<template>
  <div>

    <accounting-links v-if="isReportPage"></accounting-links>

    <div class="box" :class="{ 'py-1 px-1': compact }">

      <div class="flex flex-wrap items-center" v-if="showActions">
        <filtering-toggle-button :filtering="filtering" />

        <div class="flex-grow mb-1">
          <list-search v-model="searchInput" @search="getInvoices" />
          <p class="text-xs mt-0 ml-1" v-if="query.search !== ''">
            Searching for <span class="text-red-500 font-bold">{{ query.search }}</span>
          </p>
        </div>

        <div class="flex">
          <div
            v-if="hasPermission('invoice.create')"
            class="btn btn-purple text-sm mr-2 h-10 flex items-center px-3 mb-1"
            @click="$router.push({ name: 'invoice.create', params: customerID ? {customerID} : {} })"
          >
            <i class="fas fa-plus"></i>
            <span class="ml-2">New Invoice</span>
          </div>

          <button
            v-if="hasPermission('invoice.export')"
            type="button"
            class="btn btn-green text-sm px-3 flex items-center mb-1"
            :disabled="!exportFile.invoice.enabled"
            @click="exportInvoices"
          >
            <i class="fas fa-spinner fa-spin mr-2" v-if="!exportFile.invoice.enabled"></i>
            <span>Export</span>
          </button>

          <button class="btn w-10 h-10 flex items-center justify-center focus:outline-none hover:text-green-500 mb-1" @click="getInvoices">
            <i class="fas fa-sync-alt"></i>
          </button>
        </div>

      </div>


      <div class="flex flex-wrap">
        <div
          class="w-full lg:w-2/12 pr-2 pt-4 border-r border-theme-darker"
          v-show="filtering.active"
        >
          <invoice-filters
            :filters.sync="filters"
            @updated="getInvoices"
            @has-changes="filtering.hasChanges = $event"
          />
        </div>
        <div class="w-full overflow-x-auto" :class="{ 'lg:w-10/12': filtering.active}">

          <p class="text-lg text-center my-12" v-if="!hasInvoices">No data to display</p>
          <div v-else>
            <table class="table table-striped">
              <thead>
                <tr>
                  <th v-if="!compact">#</th>
                  <th>Contact</th>
                  <th v-if="!compact">Job</th>
                  <th>Type</th>
                  <th v-if="allPriceColumns">Discount</th>
                  <th v-if="allPriceColumns">Tax</th>
                  <th v-if="allPriceColumns">Net</th>
                  <th>Total</th>
                  <th>Created at</th>
                  <th title="Settlement date">Settl. date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(invoice, i) in invoices" :key="i">
                  <td class="font-bold" v-if="!compact">{{ invoice.id }}</td>
                  <td>
                    <a v-if="!customerID" class="text-link" @click.stop="$router.push($contactPageByType(invoice.contact, 'details'))">
                      <span v-if="!compact">
                        #{{ invoice.contact.id }}
                      </span>
                      {{ invoice.contact.first_name }}
                      {{ invoice.contact.last_name }}
                      <span v-if="!compact && invoice.contact.company_name">
                        ({{ invoice.contact.company_name }})
                      </span>
                    </a>
                  </td>
                  <td v-if="!compact">
                    <a
                      v-if="invoice.job && invoice.job.type && invoice.job.vehicle"
                      class="text-link"
                      @click.stop="$router.push('/job/details/' + invoice.job.id)"
                    >
                      <p class="mb-0">#{{ invoice.job.id }} {{invoice.job.type.name}}</p>
                      <span class="small">{{ invoice.job.vehicle.make }} {{ invoice.job.vehicle.model }}</span>
                    </a>
                    <div v-else> - </div>
                  </td>
                  <td>
                    <p
                      class="font-bold text-xs text-white py-1 px-1 rounded text-center uppercase"
                      :class="invoice.type === 'purchases' ? 'bg-red-500' : 'bg-green-500'"
                    >{{ invoice.type }}</p>
                  </td>
                  <td v-if="allPriceColumns">{{ $formatMoney(invoice.total.discount) }}</td>
                  <td v-if="allPriceColumns">{{ $formatMoney(invoice.total.tax) }}</td>
                  <td v-if="allPriceColumns">{{ $formatMoney(invoice.total.sub - invoice.total.discount) }}</td>
                  <td>
                    <span v-if="allPriceColumns">{{ $formatMoney(invoice.total.sub - invoice.total.discount + invoice.total.tax) }}</span>
                    <span v-else
                      class="font-bold"
                      v-tooltip="{ content: `
                        <strong>Discount:</strong> ${$formatMoney(invoice.total.discount)}
                        <strong>Tax:</strong> ${$formatMoney(invoice.total.tax)}
                      ` }"
                    >
                      {{ $formatMoney(invoice.total.sub - invoice.total.discount + invoice.total.tax) }}
                    </span>
                  </td>
                  <td class="text-xs">{{ $moment(invoice.created_at).format(config.dateTimeFormats.display) }}</td>
                  <td class="text-xs">
                    <span class="text-green-500 font-bold" v-if="invoice.settlement_date">
                      {{ $moment(invoice.settlement_date).format(config.dateTimeFormats.display) }}
                    </span>
                    <span class="text-white bg-red-500 leading-loose font-bold px-1" v-else>not settled</span>
                  </td>
                  <td>
                    <div class="flex">
                      <a
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="$router.push('/invoice/preview/' + invoice.id)"
                      ><i class="fas fa-info"></i></a>
                      <a
                        v-if="!invoice.settlement_date && hasPermission('invoice.edit')"
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="$router.push('/invoice/edit/' + invoice.id)"
                      ><i class="fas fa-edit"></i></a>
                      <a
                        v-if="!invoice.settlement_date && invoice.items.length <= 0 && hasPermission('invoice.delete')"
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="deleteInvoice(invoice.id)"
                      ><i class="fas fa-trash"></i></a>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>

        <div class="w-full flex justify-center">
          <pagination-component
            class="mt-8"
            v-if="hasInvoices && pagination.totalRows / pagination.perPage > 1"
            v-model="pagination.currentPage"
            :total-rows="pagination.totalRows"
            :rows-per-page="pagination.perPage"
          />
        </div>

      </div>

    </div>


  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
const FilteringToggleButton = resolve => require(['@/components/filters/FilteringToggleButton'], resolve)
const InvoiceFilters = resolve => require(['@/components/filters/Filters'], resolve)
const AccountingLinks = resolve => require(['@/components/pages/reports/AccountingLinks'], resolve)
const ListSearch = resolve => require(['@/components/ListSearch'], resolve)
const PaginationComponent = resolve => require(['@/components/Pagination'], resolve)

export default {
  props: {
    customerID: {},
    showActions: {
      type: Boolean,
      default: true,
    },
    type: String,
    isSettled: {},
    compact: Boolean,
  },
  components: {
    FilteringToggleButton,
    InvoiceFilters,
    AccountingLinks,
    ListSearch,
    PaginationComponent,
  },
  data() {
    return {
      isReportPage: false,
      invoices: [],
      jobTypes: [],
      searchInput: '',
      allPriceColumns: false,
      filtering: {
        active: false,
        hasChanges: false,
      },
      filters: {
        type: {
          key: 'type',
          name: 'Type',
          active: true,
          options: [
            {value: 'sales', class: 'btn-green'},
            {value: 'purchases', class: 'btn-red'},
          ],
          value: null,
        },
        createdAt: {
          key: 'created_at',
          name: 'Created at date',
          active: true,
          flatpickr: {
            mode: 'range',
            static: false,
          },
          value: null,
        },
        settled: {
          key: 'settled',
          name: 'Settled',
          active: true,
          options: [
            {name: 'Settled', value: true, class: 'btn-green'},
            {name: 'Not Settled', value: false, class: 'btn-red'},
          ],
          value: null,
          children: [
            {
              key: 'settlement_date',
              name: 'Settlement date',
              active: true,
              showWhenParentValue: true,
              flatpickr: {
                mode: 'range',
                static: false,
              },
              value: null,
            }
          ]
        },
        cashOnDelivery: {
          key: 'cash_on_delivery',
          name: 'Cash on delivery',
          active: true,
          options: [
            {name: 'Cash', value: true, class: 'btn-green'},
            {name: 'Credit', value: false, class: 'btn-red'},
          ],
          value: null,
        },
      },
      query: {
        search: '',
      },
      exportFile: {
        invoice: {
          enabled: true,
        }
      },
      pagination: {
        currentPage: 1,
        totalRows: 0,
        perPage: 0,
      }
    }
  },
  watch: {
    'pagination.currentPage'() {
      this.getInvoices()
    },
    searchInput(i) {
      if (i === '' && this.query.search !== '') {
        this.getInvoices()
      }
    },
    invoices() {
      for (let i in this.invoices) {
        const total = {
          sub: 0,
          discount: 0,
          tax: 0,
        }
        this.invoices[i].items.map(v => {
          v.total = {
            raw: v.value * v.quantity,
          }
          v.total.discount = v.total.raw * v.discount / 100
          v.total.tax = (v.total.raw - v.total.discount) * v.tax / 100
          v.total.all = v.total.raw - v.total.discount + v.total.tax
          total.sub += v.total.raw
          total.discount += v.total.discount
          total.tax += v.total.tax
        })
        this.invoices[i].total = total
      }
    },
  },
  computed: {
    hasInvoices() {
      return Object.entries(this.invoices).length > 0
    },
    ...mapGetters({
      config: 'config/config',
      hasPermission: 'user/hasPermission',
    })
  },
  created() {
    this.setRouteFilters()
  },
  mounted() {
    if (this.$route.name.split('.')[0] === 'report') {
      this.isReportPage = true
      this.allPriceColumns = true
    }

    // if (this.isSettled === false || this.isSettled === true) {
    if (typeof this.isSettled === 'boolean') {
      this.filters.settled.value = this.isSettled
    }
    this.getInvoices()
  },
  methods: {
    _setRouteFilters(routeFilters) {
      for (let name in routeFilters) {
        for (let i in this.filters) {
          if (this.filters[i].key === name) {
            Object.assign(this.filters[i], routeFilters[name])
          }
        }
        if (routeFilters.children) {
          this._setRouteFilters(routeFilters.children)
        }
      }
    },
    setRouteFilters() {
      if (!this.$route.meta.filters) {
        return
      }
      let routeFilters = this.$route.meta.filters
      this._setRouteFilters(routeFilters)
    },
    exportInvoices() {
      this.exportFile.invoice.enabled = false

      let filters = this.$getFilterValues(this.filters)
      if (filters.created_at) {
        let createdAt = this.$getRangeDates(filters.created_at, this.config)
        filters.dateFrom = createdAt.from
        filters.dateTo = createdAt.to
      }
      if (filters.settlement_date) {
        let settlementDate = this.$getRangeDates(filters.settlement_date, this.config)
        filters.settlementDateFrom = settlementDate.from
        filters.settlementDateTo = settlementDate.to
      }

      axios.get('invoices/export', {
        params: {
          search: this.searchInput,
          filters,
        },
        responseType: 'arraybuffer'
      })
        .then((response) => {
          this.$downloadFile(response)
          this.exportFile.invoice.enabled = true
        })
    },
    deleteInvoice(invoiceID) {
      this.$swal.fire({
        title: 'Delete invoice',
        text: 'Are you sure?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete'
      })
        .then((result) => {
          if (result.value) {
            // send delete
            axios.delete('invoices/' + invoiceID)
              .then(() => {
                this.$swal.fire('Delete successful', '', 'success')
                this.getInvoices()
              })
          }
        })
    },
    getJobTypes() {
      axios.get('job-types')
        .then((response) => {
          this.jobTypes = response.data
          for (let i in this.invoices) {
            const inv = this.invoices[i]
            if (inv.job) {
              for (let j in this.jobTypes) {
                if (this.jobTypes[j].id === inv.job.job_type_id) {
                  this.$set(inv.job, 'type', this.jobTypes[j])
                }
              }
            }
          }
        })
    },
    getInvoices() {
      let filters = this.$getFilterValues(this.filters)
      if (filters.created_at) {
        let createdAt = this.$getRangeDates(filters.created_at, this.config)
        filters.dateFrom = createdAt.from
        filters.dateTo = createdAt.to
      }
      if (filters.settlement_date) {
        let settlementDate = this.$getRangeDates(filters.settlement_date, this.config)
        filters.settlementDateFrom = settlementDate.from
        filters.settlementDateTo = settlementDate.to
      }

      axios.get('invoices', {params: {
        page: this.pagination.currentPage,
        search: this.searchInput,
        filters,
      }})
        .then(response => {
          if (response.data) {
            this.getJobTypes()

            const res = response.data
            this.pagination.perPage = res.per_page
            this.pagination.totalRows = res.total
            this.invoices = Object.assign({}, res.data)

            this.query.search = this.searchInput
          }
        })
    }
  }
}
</script>
